import {ClassNameProps} from '@app/helpers/react';
import FaIcon, {CustomIconName} from '@app/lui3/FaIcon';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {forwardRef, InputHTMLAttributes, ReactNode} from 'react';

import Icon, {IconType} from '../Icon';
import Spinner from '../Spinner';
import InputWrapper, {
  InlineInputLabel,
  LengthIndicator,
  SupportingText,
} from './InputWrapper';

type Size = 'small' | 'compact' | 'default' | 'big' | 'huge';

type Style = 'filled' | 'outline' | 'seamless';

type Radius = 'square' | 'small' | 'normal';

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    ClassNameProps {
  disabled?: boolean;
  size?: Size;
  minWidth?: string;
  label?: ReactNode;
  supportingText?: ReactNode;
  beforeIcon?: IconType;
  beforeFaIcon?: IconName | CustomIconName;
  before?: ReactNode;
  afterIcon?: IconType;
  afterFaIcon?: IconName | CustomIconName;
  after?: ReactNode;
  variant?: Style;
  compact?: boolean;
  error?: ReactNode;
  warning?: ReactNode;
  loading?: boolean;
  square?: boolean;
  radius?: Radius;
  bold?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  forceHoverState?: boolean;
}

const Input = forwardRef<HTMLElement, Props>(
  (
    {
      id,
      label,
      maxLength,
      error,
      warning,
      supportingText,
      beforeIcon,
      beforeFaIcon,
      before,
      afterIcon,
      afterFaIcon,
      after,
      loading,
      variant = 'outline',
      compact,
      size = compact ? 'compact' : 'default',
      minWidth = '9ch',
      value,
      style,
      inputRef,
      square,
      forceHoverState,
      className,
      radius = 'normal',
      bold = false,
      ...rest
    },
    ref
  ) => (
    <InputWrapper
      ref={ref}
      htmlFor={id}
      data-size={size}
      data-style={variant}
      data-square={square}
      data-state={error ? 'error' : warning ? 'warning' : undefined}
      data-radius={radius}
      data-bold={!!bold && !!value}
      data-force-hover={forceHoverState ? 'true' : undefined}
      className={className}
      style={{minWidth, ...style}}
    >
      {(forId) => (
        <>
          {label && <InlineInputLabel>{label}</InlineInputLabel>}
          {beforeIcon && <Icon icon={beforeIcon} />}
          {beforeFaIcon && (
            <FaIcon icon={beforeFaIcon} faStyle="solid" color="gray-600" />
          )}
          {before}
          <input
            ref={inputRef}
            value={value}
            id={forId}
            maxLength={maxLength}
            size={1}
            {...rest}
          />
          {loading && (
            <span style={{opacity: 0.5}}>
              <Spinner />
            </span>
          )}
          {after}
          {maxLength && (
            <LengthIndicator>
              {typeof value === 'string' ? value.length : 0}/{maxLength}
            </LengthIndicator>
          )}
          {afterIcon && !error && !warning && <Icon icon={afterIcon} />}
          {afterFaIcon && !error && !warning && (
            <FaIcon icon={afterFaIcon} faStyle="solid" color="gray-600" />
          )}
          {warning && !error && <Icon icon="warning" color="warn" />}
          {error && <Icon icon="error" color="error" />}
          {((error && typeof error !== 'boolean') ||
            (warning && typeof warning !== 'boolean') ||
            supportingText) && (
            <SupportingText>
              {(typeof error !== 'boolean' ? error : false) ||
                (typeof warning !== 'boolean' ? warning : false) ||
                supportingText}
            </SupportingText>
          )}
        </>
      )}
    </InputWrapper>
  )
);

export default Input;
